import { useTranslation } from '@govard/i18n-core/react';
import type { UiCommandRequest } from '../../../agents/context/ui-command-registry.js';
import { useState } from 'react';
import { User, Kanban } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@govard/ui-primitives';

import { useApprovalMutation } from '../hooks/useApprovalMutation.js';
import type { ApprovalItem } from '../../approvals/api/approvals.client.js';
import { summarizeBulkApproval } from '../../approvals/lib/bulkApprovalSummary.js';
import { ApprovalDetailText } from '../../approvals/lib/ApprovalDetailText.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../../shared/api-client/index.js';
import { getApprovalById } from '../../approvals/api/approvals.client.js';
import { getTaskProposalForm } from '../api/taskProposal.client.js';
import { TaskProposalEditor } from './TaskProposalEditor.js';
import {
  approvalConsistencyWarnings,
  summarizeApprovalPresentation,
  summarizeLegacyApproval,
} from '../../approvals/lib/approvalSummary.js';

interface AiApprovalCardProps {
  approval: ApprovalItem;
  summary: string;
  diff: string;
  onExecutePrompt?: ((prompt: string) => void) | undefined;
  onExecuteCommand?: ((command: UiCommandRequest) => void) | undefined;
  onOpenEntity?: ((type: 'deal' | 'contact', id: string, title?: string) => void) | undefined;
  onDecisionApplied?: ((approval: ApprovalItem) => void) | undefined;
}

export function AiApprovalCard({
  approval,
  onExecuteCommand,
  onOpenEntity,
  onDecisionApplied,
}: AiApprovalCardProps) {
  const { t, locale } = useTranslation();
  const queryClient = useQueryClient();
  const approvalMutation = useApprovalMutation();
  const [resolvedApproval, setResolvedApproval] = useState<ApprovalItem | null>(null);
  const [proposalUnsaved, setProposalUnsaved] = useState(false);
  const [savedProposal, setSavedProposal] = useState<Awaited<
    ReturnType<typeof getTaskProposalForm>
  > | null>(null);
  const isTaskProposal = ['create_task', 'update_task'].includes(
    typeof approval.payload.toolName === 'string' ? approval.payload.toolName : '',
  );
  const {
    data: proposal,
    error: proposalError,
    refetch: refreshProposal,
    isLoading: proposalLoading,
  } = useQuery({
    queryKey: ['task-proposal', approval.id],
    queryFn: () => getTaskProposalForm(approval.id),
    enabled: isTaskProposal,
    retry: false,
  });
  const activeProposal = savedProposal ?? proposal;
  const proposalReadFailed =
    proposalError !== null &&
    (!(proposalError instanceof ApiError) || proposalError.status !== 404);
  const { data: latestApproval } = useQuery({
    queryKey: ['approval', activeProposal?.approvalId],
    queryFn: () => getApprovalById(activeProposal?.approvalId ?? ''),
    enabled: activeProposal?.approvalId != null && activeProposal.approvalId !== approval.id,
    retry: false,
  });

  async function handleDecision(decision: 'approve' | 'reject') {
    // The mutation's onError already reports the failure in a toast; the
    // catch only keeps the rejected promise from surfacing as an unhandled
    // rejection from the click handler.
    try {
      const updatedApproval = await approvalMutation.mutateAsync({
        approvalId: currentApproval.id,
        decision,
      });
      setResolvedApproval(updatedApproval);
      void queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      onDecisionApplied?.(updatedApproval);
    } catch {
      // reported by useApprovalMutation onError
    }
  }

  const currentApproval = resolvedApproval ?? latestApproval ?? approval;
  const safeCopy = currentApproval.presentation
    ? summarizeApprovalPresentation(currentApproval.presentation)
    : summarizeLegacyApproval(currentApproval);
  const displaySummary = safeCopy.action;
  const bulk = summarizeBulkApproval(currentApproval);
  const displayDiff =
    bulk !== null && !bulk.countKnown
      ? safeCopy.action
      : safeCopy.details.map((detail) => `${detail.label}: ${detail.value}`).join(' · ') ||
        (currentApproval.presentation?.changes.length === 0
          ? currentApproval.presentation.target.label
          : safeCopy.action);
  const safeEntityLabel =
    safeCopy.presentationTarget?.label ?? safeCopy.target?.label ?? safeCopy.action;
  const consistencyWarnings = approvalConsistencyWarnings(currentApproval);
  const isResolved = currentApproval.status !== 'pending';
  const executionFailed = currentApproval.executionOutcome?.status === 'failed';
  const executionDealStageStale =
    executionFailed && currentApproval.executionOutcome?.errorCode === 'stale_deal_stage';
  const executionWriteStale =
    executionFailed && currentApproval.executionOutcome?.errorCode === 'stale_write';
  const executionStale = executionDealStageStale || executionWriteStale;
  const executionRetryRequired =
    executionFailed &&
    ['lease_expired', 'scope_recheck_failed'].includes(
      currentApproval.executionOutcome?.errorCode ?? '',
    );
  const executionSucceeded =
    currentApproval.status === 'approved' &&
    currentApproval.executionOutcome?.status === 'succeeded' &&
    (bulk === null || bulk.result !== null);
  const executionExecuting =
    currentApproval.status === 'approved' &&
    currentApproval.executionOutcome?.status === 'executing';
  const executionPendingVerification =
    currentApproval.status === 'approved' &&
    !executionFailed &&
    !executionSucceeded &&
    !executionExecuting;
  const executionPartial =
    currentApproval.status === 'approved' &&
    bulk?.result != null &&
    bulk.result.unfinished === 0 &&
    bulk.result.succeeded > 0 &&
    bulk.result.failed + bulk.result.stale + bulk.result.skipped > 0;
  const bulkExecutionFailed =
    currentApproval.status === 'approved' &&
    bulk?.result != null &&
    bulk.result.unfinished === 0 &&
    bulk.result.succeeded === 0 &&
    bulk.result.failed + bulk.result.stale + bulk.result.skipped > 0;
  const bulkExecutionExecuting =
    currentApproval.status === 'approved' && bulk?.result != null && bulk.result.unfinished > 0;
  const toolName =
    typeof currentApproval.payload.toolName === 'string' ? currentApproval.payload.toolName : null;
  const canUndoCreatedTask =
    executionSucceeded &&
    toolName === 'create_task' &&
    currentApproval.executionResult != null &&
    onExecuteCommand !== undefined;
  const staleLabelKey = executionWriteStale
    ? 'crm.approvals.execution_stale_write'
    : 'crm.approvals.execution_stale';
  const staleDetailsKey = executionWriteStale
    ? 'crm.approvals.execution_stale_write_details'
    : 'crm.approvals.execution_stale_details';
  const resolutionLabel = executionStale
    ? t(staleLabelKey)
    : executionRetryRequired
      ? t('crm.approvals.bulk.status.retry')
      : bulkExecutionExecuting
        ? t('crm.approvals.execution_executing')
        : bulkExecutionFailed
          ? t('crm.approvals.execution_failed')
          : executionPartial
            ? t('crm.approvals.bulk.status.partial')
            : executionFailed
              ? t('crm.approvals.execution_failed')
              : executionExecuting
                ? t('crm.approvals.execution_executing')
                : executionSucceeded
                  ? t('crm.approvals.execution_succeeded')
                  : executionPendingVerification
                    ? t('crm.approvals.execution_pending_verification')
                    : currentApproval.status === 'approved'
                      ? t('crm.approvals.approved')
                      : currentApproval.status === 'rejected'
                        ? t('crm.approvals.rejected')
                        : currentApproval.status === 'expired'
                          ? t('crm.approvals.expired')
                          : t('crm.approvals.cancelled');

  return (
    <Card data-testid={bulk ? 'bulk-approval-card' : `approval-card-${approval.id}`}>
      <CardHeader>
        <CardTitle>{displaySummary}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {currentApproval.presentation?.target.kind === 'deal' ? (
          <p className="break-words text-sm text-muted-foreground">
            {t('crm.approvals.detail.deal')}: {currentApproval.presentation.target.label}
          </p>
        ) : null}
        {currentApproval.presentation?.target.kind === 'memory' ? (
          <div className="space-y-2">
            {safeCopy.details.map((detail, index) => (
              <div key={`${detail.label}:${index}`} className="min-w-0">
                <ApprovalDetailText detail={detail} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{displayDiff}</p>
        )}
        {proposalReadFailed ? (
          <p role="alert" className="text-sm text-destructive">
            {t('crm.ai_chat.proposal.load_error')}
          </p>
        ) : null}
        {activeProposal ? (
          <TaskProposalEditor
            key={activeProposal.revisionId}
            proposal={activeProposal}
            onDirtyChange={setProposalUnsaved}
            onSaved={(next) => {
              setSavedProposal(next);
              void refreshProposal();
              void queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
              void queryClient.invalidateQueries({ queryKey: ['approvals'] });
            }}
          />
        ) : null}
        {consistencyWarnings.length > 0 ? (
          <div
            data-testid="approval-consistency-warnings"
            className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700/60 dark:bg-amber-950/30"
          >
            <p className="font-medium text-amber-800 dark:text-amber-200">
              {t('crm.approvals.consistency.title')}
            </p>
            <ul className="list-disc pl-4 text-amber-800 dark:text-amber-200">
              {consistencyWarnings.map((warning) => (
                <li key={`${warning.field}:${warning.kind}`}>
                  {warning.kind === 'missing'
                    ? t('crm.approvals.consistency.missing', {
                        field: warning.field,
                        expected: warning.expected,
                      })
                    : t('crm.approvals.consistency.mismatch', {
                        field: warning.field,
                        expected: warning.expected,
                        actual: warning.actual ?? '',
                      })}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {bulk ? (
          <div data-testid="bulk-approval-preview" className="space-y-3 rounded-lg border p-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              {bulk.countKnown ? (
                <strong>{t('crm.approvals.bulk.count', { count: bulk.count })}</strong>
              ) : null}
              <span className="text-muted-foreground">
                {t('crm.approvals.bulk.partial_semantics')}
              </span>
            </div>
            {bulk.excluded > 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('crm.approvals.bulk.excluded', { count: bulk.excluded })}
              </p>
            ) : null}
            {bulk.relationType ? (
              <p className="text-sm text-muted-foreground">
                {t('crm.approvals.bulk.relation_type', {
                  relationType: t(`crm.approvals.bulk.relation.${bulk.relationType}`),
                })}
              </p>
            ) : null}
            {bulk.downstreamImpact.length > 0 ? (
              <p data-testid="bulk-downstream-impact" className="text-sm text-muted-foreground">
                {t('crm.approvals.impact.label')}:{' '}
                {bulk.downstreamImpact
                  .map(({ key, count }) => t(`crm.approvals.impact.${key}`, { count }))
                  .join(', ')}
              </p>
            ) : null}
            {bulk.sample.length > 0 ? (
              <ol data-testid="bulk-approval-sample" className="space-y-1 text-sm">
                {bulk.sample.map((item) => (
                  <li
                    key={item.ordinal}
                    className="flex min-w-0 items-center justify-between gap-3"
                  >
                    <span className="truncate">
                      {item.label ?? t('crm.approvals.bulk.item', { number: item.ordinal + 1 })}
                    </span>
                    {item.relations.length > 0 ? (
                      <span
                        data-testid="bulk-approval-sample-relations"
                        className="flex shrink-0 items-center gap-1"
                      >
                        {item.relations.map((relation) => (
                          <Badge
                            key={relation}
                            data-testid={`bulk-approval-sample-relation-${relation}`}
                            variant="outline"
                            className="text-[10px] font-normal"
                          >
                            {t(`crm.approvals.bulk.relation.${relation}`)}
                          </Badge>
                        ))}
                      </span>
                    ) : null}
                    {item.status ? (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {t(`crm.approvals.bulk.item_status.${item.status}`)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : null}
            {bulk.capped ? (
              <p className="text-xs text-muted-foreground">
                {t('crm.approvals.bulk.sample_capped', {
                  count: 20,
                  remaining: Math.max(0, bulk.count - bulk.sample.length),
                })}
              </p>
            ) : null}
            {bulk.result ? (
              <p data-testid="bulk-approval-result" className="text-sm text-muted-foreground">
                {t('crm.approvals.bulk.result', bulk.result)}
              </p>
            ) : null}
            {bulk.operation === 'delete' && !isResolved ? (
              <p data-testid="bulk-delete-warning" className="text-sm font-medium text-destructive">
                {t('crm.approvals.bulk.delete_warning', { count: bulk.count })}
              </p>
            ) : null}
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {t('crm.approvals.expires_at')}{' '}
          {new Date(currentApproval.expiresAt).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US')}
        </p>
        {executionStale ? (
          <p data-testid="chat-approval-stale" className="text-sm text-destructive">
            {t(staleDetailsKey)}
          </p>
        ) : executionRetryRequired ? (
          <p data-testid="chat-approval-retry" className="text-sm text-destructive">
            {t('crm.approvals.bulk.retry_details')}
          </p>
        ) : executionFailed ? (
          <p data-testid="chat-approval-execution-error" className="text-sm text-destructive">
            {t('crm.approvals.execution_failed_details')}
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="flex flex-col items-stretch gap-2 min-[375px]:flex-row min-[375px]:flex-wrap min-[375px]:items-center min-[375px]:gap-3">
        {isResolved ? (
          <>
            <Badge
              data-testid="chat-approval-status"
              className="whitespace-nowrap"
              variant={
                executionFailed || bulkExecutionFailed
                  ? 'destructive'
                  : executionSucceeded && !executionPartial && !bulkExecutionExecuting
                    ? 'success'
                    : 'outline'
              }
            >
              {resolutionLabel}
            </Badge>
            {canUndoCreatedTask ? (
              <Button
                data-testid="chat-approval-undo"
                variant="outline"
                onClick={() =>
                  onExecuteCommand({
                    commandId: 'undo_created_task',
                    params: { sourceApprovalId: currentApproval.id },
                  })
                }
              >
                {t('crm.approvals.undo')}
              </Button>
            ) : null}
            {executionSucceeded &&
              onOpenEntity &&
              currentApproval.executionResult != null &&
              (toolName === 'create_contact' || toolName === 'update_contact') && (
                <Button
                  data-testid="chat-approval-open-contact"
                  variant="outline"
                  onClick={() => {
                    const rawPayload = currentApproval.payload as {
                      args?: Record<string, unknown>;
                    };
                    const rawExec = currentApproval.executionResult as
                      | Record<string, unknown>
                      | null
                      | undefined;
                    const rawArgs = rawPayload?.args ?? {};
                    const contactId = String(
                      rawExec?.['contactId'] ?? rawExec?.['id'] ?? rawArgs['contactId'] ?? '',
                    );
                    const firstName =
                      typeof rawArgs['firstName'] === 'string' ? rawArgs['firstName'] : '';
                    const lastName =
                      typeof rawArgs['lastName'] === 'string' ? rawArgs['lastName'] : '';
                    const name =
                      [firstName, lastName].filter(Boolean).join(' ') ||
                      String(rawArgs['name'] ?? safeEntityLabel);
                    if (contactId) onOpenEntity('contact', contactId, name);
                  }}
                >
                  <User size={13} className="mr-1 text-blue-600" />
                  {t('crm.ai_chat.overlay.open_contact')}
                </Button>
              )}
            {executionSucceeded &&
              onOpenEntity &&
              currentApproval.executionResult != null &&
              (toolName === 'create_deal' ||
                toolName === 'update_deal' ||
                toolName === 'update_deal_stage') && (
                <Button
                  data-testid="chat-approval-open-deal"
                  variant="outline"
                  onClick={() => {
                    const rawPayload = currentApproval.payload as {
                      args?: Record<string, unknown>;
                    };
                    const rawExec = currentApproval.executionResult as
                      | Record<string, unknown>
                      | null
                      | undefined;
                    const rawArgs = rawPayload?.args ?? {};
                    const dealId = String(
                      rawExec?.['dealId'] ?? rawExec?.['id'] ?? rawArgs['dealId'] ?? '',
                    );
                    const title = String(
                      rawArgs['title'] ?? rawArgs['dealTitle'] ?? safeEntityLabel,
                    );
                    if (dealId) onOpenEntity('deal', dealId, title);
                  }}
                >
                  <Kanban size={13} className="mr-1 text-amber-600" />
                  {t('crm.ai_chat.overlay.open_deal')}
                </Button>
              )}
          </>
        ) : (
          <>
            <Badge
              data-testid="chat-approval-status"
              variant="outline"
              className="whitespace-nowrap"
            >
              {t('crm.approvals.pending')}
            </Badge>
            <Button
              data-testid="chat-approval-reject"
              disabled={approvalMutation.isPending || proposalUnsaved}
              onClick={() => void handleDecision('reject')}
              variant="outline"
            >
              {t('crm.approvals.reject')}
            </Button>
            <Button
              data-testid="chat-approval-approve"
              disabled={
                approvalMutation.isPending ||
                proposalUnsaved ||
                (isTaskProposal && proposalLoading) ||
                proposalReadFailed ||
                (activeProposal !== undefined &&
                  (!activeProposal.editable || activeProposal.approvalId !== currentApproval.id))
              }
              onClick={() => void handleDecision('approve')}
              variant="primary"
            >
              {bulk?.operation === 'delete'
                ? t('crm.approvals.bulk.confirm_delete', { count: bulk.count })
                : t('crm.approvals.approve')}
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}
